import { useNavigate } from 'react-router-dom';
import { usePharmacySupplierInvoice } from '../../services/httpServices/queries';
import type { PharmacySupplierInvoice } from '../../services/httpServices/pharmacyPurchasingService';
import { Loader2, AlertCircle, X, ReceiptText } from 'lucide-react';

const statusStyles: Record<string, string> = {
  UNPAID: 'bg-danger/10 text-danger',
  PARTIAL: 'bg-warning/10 text-warning',
  PAID: 'bg-success/10 text-success',
  OVERDUE: 'bg-danger/10 text-danger',
};

type InvoiceWithPayments = PharmacySupplierInvoice & {
  payments?: Array<{
    id: string;
    amount: number;
    paidAt?: string;
    method?: string;
    reference?: string;
  }>;
};

/** Read-only popup showing a supplier invoice with its payments/receipts. */
export function PharmacyInvoiceDetailsModal({
  invoiceId,
  onClose,
}: {
  invoiceId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { data, isLoading, error } = usePharmacySupplierInvoice(invoiceId);
  const invoice = data?.data as InvoiceWithPayments | undefined;

  const balance = invoice
    ? Number(
        invoice.balance ??
          Number(invoice.amount) - Number(invoice.paidAmount) - Number(invoice.creditedAmount ?? 0),
      )
    : 0;

  const fmtDate = (d?: string) => (d ? new Date(d).toLocaleDateString() : '—');
  const fmtMoney = (n?: number | string) =>
    Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-surface border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <ReceiptText size={18} className="text-primary" />
            <h2 className="text-lg font-semibold text-text">Invoice Details</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-background text-text-muted">
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
          </div>
        ) : error || !invoice ? (
          <div className="p-8 text-center text-danger">
            <AlertCircle size={18} className="inline mr-2" /> Failed to load invoice
          </div>
        ) : (
          <div className="p-4 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase text-text-muted">Invoice</div>
                <div className="font-mono text-lg font-semibold text-text">{invoice.invoiceNumber}</div>
                <div className="text-sm text-text-muted mt-1">{invoice.supplier?.name || '—'}</div>
              </div>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusStyles[invoice.status]}`}>
                {invoice.isOverdue && invoice.status !== 'PAID' ? 'OVERDUE' : invoice.status}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <div className="text-xs uppercase text-text-muted">Invoice Date</div>
                <div className="text-text">{fmtDate(invoice.invoiceDate)}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-text-muted">Due Date</div>
                <div className="text-text">{fmtDate(invoice.dueDate)}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-text-muted">Receipt (GRN)</div>
                {invoice.grn?.grnNumber ? (
                  <button
                    onClick={() => navigate(`/pharmacy/receipts/${invoice.grn!.id}`)}
                    className="font-mono text-primary hover:underline"
                  >
                    {invoice.grn.grnNumber}
                  </button>
                ) : invoice.grnId ? (
                  <button
                    onClick={() => navigate(`/pharmacy/receipts/${invoice.grnId}`)}
                    className="text-primary hover:underline"
                  >
                    View receipt
                  </button>
                ) : (
                  <div className="text-text-muted">—</div>
                )}
              </div>
            </div>

            <div className="border border-border rounded-lg divide-y divide-border text-sm">
              <div className="flex justify-between px-3 py-2">
                <span className="text-text-muted">Amount</span>
                <span className="text-text">{fmtMoney(invoice.amount)}</span>
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-text-muted">Paid</span>
                <span className="text-text">{fmtMoney(invoice.paidAmount)}</span>
              </div>
              <div className="flex justify-between px-3 py-2">
                <span className="text-text-muted">Credited (returns)</span>
                <span className="text-text">{fmtMoney(invoice.creditedAmount)}</span>
              </div>
              <div className="flex justify-between px-3 py-2 bg-background font-semibold">
                <span className="text-text">Balance</span>
                <span className="text-text">{fmtMoney(balance)}</span>
              </div>
            </div>

            {invoice.payments && invoice.payments.length > 0 && (
              <div>
                <div className="text-sm font-medium text-text mb-2">Payment Receipts</div>
                <div className="border border-border rounded-lg divide-y divide-border text-sm">
                  {invoice.payments.map((p) => (
                    <div key={p.id} className="flex justify-between px-3 py-2">
                      <span className="text-text-muted">
                        {fmtDate(p.paidAt)} · {(p.method || '').replace('_', ' ')}
                        {p.reference ? ` · ${p.reference}` : ''}
                      </span>
                      <span className="text-text">{fmtMoney(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {invoice.notes && (
              <div>
                <div className="text-xs uppercase text-text-muted">Notes</div>
                <div className="text-sm text-text whitespace-pre-wrap">{invoice.notes}</div>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
